export type FilterValue = string | string[];

/** Mirrors `Flop.Filter.op/0` (apps/phoenix/deps/flop/lib/flop/filter.ex). */
export type FilterOp =
  | '=='
  | '!='
  | '=~'
  | 'empty'
  | 'not_empty'
  | '<='
  | '<'
  | '>='
  | '>'
  | 'in'
  | 'not_in'
  | 'contains'
  | 'not_contains'
  | 'like'
  | 'not_like'
  | 'like_and'
  | 'like_or'
  | 'ilike'
  | 'not_ilike'
  | 'ilike_and'
  | 'ilike_or'
  | 'starts_with'
  | 'ends_with';

export interface FilterScope {
  nursingHomeId?: string;
  legalEntityId?: string;
}

/**
 * Builds `filters[i][field]/[op]/[value]` Flop query params, tracking the
 * index internally so call sites never touch it. A `value` array (even a
 * single-element one) is sent as `filters[i][value][]`; omitting `value`
 * sends a filter with no value, for ops like `empty`/`not_empty` that don't
 * need one.
 *
 * @example
 * const query = new Filter()
 *   .buildScope(useScopeFilter())
 *   .field('resident_id', '==', residentId)
 *   .field('type', 'in', types)
 *   .build();
 */
export class Filter {
  private readonly query = new URLSearchParams();
  private index = 0;

  field(field: string, op: FilterOp, value?: FilterValue): this {
    this.query.append(`filters[${this.index}][field]`, field);
    this.query.append(`filters[${this.index}][op]`, op);
    if (Array.isArray(value)) {
      for (const item of value) {
        this.query.append(`filters[${this.index}][value][]`, item);
      }
    } else if (value !== undefined) {
      this.query.append(`filters[${this.index}][value]`, value);
    }
    this.index++;
    return this;
  }

  /**
   * Like {@link field}, but skips the filter entirely when `value` is falsy
   * (undefined, null, '') or an empty array.
   *
   * Do NOT use this for an array param where an empty array is meant to
   * match zero rows (e.g. a `selected ? [selected.id] : []` picker guard,
   * common on `in` filters) — an empty array here is treated the same as
   * "no filter", which shows *everything* instead of nothing. That call
   * site wants `if (value) { filter.field(field, 'in', value); }` instead,
   * so a present-but-empty array still sends the (valueless) `in` filter.
   * Only use `fieldIfPresent` where "not provided" and "provided but empty"
   * should behave identically (i.e. the original guard was already
   * `if (x && x.length > 0)`, or `value` is a scalar).
   *
   * For a filter whose presence depends on something other than the value
   * itself (e.g. null has its own meaning, or a boolean flag maps to a
   * fixed value), use `field` directly.
   */
  fieldIfPresent(
    field: string,
    op: FilterOp,
    value: FilterValue | null | undefined
  ): this {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return this;
    }
    return this.field(field, op, value);
  }

  buildScope(scope: FilterScope): this {
    if (scope.nursingHomeId) {
      this.field('nursing_home_id', '==', scope.nursingHomeId);
    }
    if (scope.legalEntityId) {
      this.field('legal_entity_id', '==', scope.legalEntityId);
    }
    return this;
  }

  build(): URLSearchParams {
    return this.query;
  }
}
